import PatientService from '../../../services/patient.service';
import { allPractices, CaseStatusFilter, CaseSummary, PracticeSelection, resolvePractice, toCaseSummary } from './caseSummary';
import { CaseTimingService, createCaseTimingService, withCaseTiming } from './caseTimingService';

export interface CaseDetailsService {
  listRecent(take?: number): Promise<CaseSummary[]>;
  findByRefNo(refNo: string): Promise<CaseSummary | null>;
  listByStatus(statusFilter: CaseStatusFilter, take?: number): Promise<CaseSummary[]>;
  /** Drops every cached list so the next lookup hits the API again (e.g. when the widget is reopened). */
  invalidate(): void;
}

type CaseFetcher = (args: {
  personnelId: string[];
  practiceId: string;
  search?: string;
  statusFilter?: string;
  skip?: number;
  take?: number;
}) => Promise<Response>;

interface FetchArgs {
  search?: string;
  statusFilter?: string;
  take?: number;
}

/** Every list is reused for this long; the widget clears the cache when it is reopened. */
export const LIST_CACHE_TTL_MS = 5 * 60 * 1000;
/** How many rows to pull from each practice before merging; enough to fill the list after dropping completed ones. */
const RECENT_FETCH_PER_PRACTICE = 20;
const DEFAULT_TAKE = 20;

const isCompleted = (c: CaseSummary): boolean =>
  c.statusLabel === 'Completed' || c.rawStatus === 'Completed' || c.rawStatus === 'Received';

const updatedAtMs = (c: CaseSummary): number => {
  const ms = c.lastUpdated ? Date.parse(c.lastUpdated) : NaN;
  return Number.isNaN(ms) ? 0 : ms;
};

const byNewest = (a: CaseSummary, b: CaseSummary) => updatedAtMs(b) - updatedAtMs(a);

async function readCaseList(response: Response): Promise<any[]> {
  if (!response || !response.ok) {
    throw new Error(`Case lookup failed (${response ? response.status : 'no response'})`);
  }
  const json = await response.json();
  return Array.isArray(json?.caseList) ? json.caseList : [];
}

interface CacheEntry {
  at: number;
  promise: Promise<CaseSummary[]>;
  /** Set once the promise resolves, so cached rows can be scanned without awaiting. */
  cases?: CaseSummary[];
}

export function createCaseDetailsService(
  userInfo: any,
  fetcher: CaseFetcher = PatientService.getCasesByPersonnel,
  now: () => number = Date.now,
  timing: CaseTimingService = createCaseTimingService()
): CaseDetailsService {
  const practice = resolvePractice(userInfo);
  const practices = allPractices(userInfo);
  /** One cache for every lookup, keyed by practice + filter + search + page size; in-flight requests are shared. */
  const cache = new Map<string, CacheEntry>();

  const keyFor = (scope: PracticeSelection, args: FetchArgs): string =>
    [scope.practiceId, args.statusFilter ?? '', args.search ?? '', args.take ?? DEFAULT_TAKE].join('|');

  const isFresh = (entry: CacheEntry): boolean => now() - entry.at < LIST_CACHE_TTL_MS;

  const fetchCases = (args: FetchArgs, scope: PracticeSelection): Promise<CaseSummary[]> => {
    const key = keyFor(scope, args);
    const hit = cache.get(key);
    if (hit && isFresh(hit)) return hit.promise;

    const entry: CacheEntry = { at: now(), promise: Promise.resolve([]) };
    entry.promise = fetcher({
      personnelId: scope.personnelIds,
      practiceId: scope.practiceId,
      search: args.search ?? '',
      statusFilter: args.statusFilter ?? '',
      skip: 0,
      take: args.take ?? DEFAULT_TAKE,
    })
      .then(readCaseList)
      .then((rows) => withCaseTiming(rows.filter((r) => r?.caseRefNo).map(toCaseSummary), timing))
      .then((cases) => {
        entry.cases = cases;
        return entry.cases;
      })
      .catch((err) => {
        // A failed request must not be served from the cache.
        if (cache.get(key) === entry) cache.delete(key);
        throw err;
      });
    cache.set(key, entry);
    return entry.promise;
  };

  const fetchAll = (args: FetchArgs): Promise<CaseSummary[][]> =>
    Promise.all(practices.map((scope) => fetchCases(args, scope).catch(() => [] as CaseSummary[])));

  /** Every case already sitting in a fresh cache entry, selected practice first. */
  const cachedCases = (): CaseSummary[] => {
    const ordered = practice ? [practice.practiceId, ...practices.map((p) => p.practiceId).filter((id) => id !== practice.practiceId)] : [];
    const found: CaseSummary[] = [];
    ordered.forEach((practiceId) => {
      cache.forEach((entry, key) => {
        if (entry.cases && isFresh(entry) && key.startsWith(`${practiceId}|`)) found.push(...entry.cases);
      });
    });
    return found;
  };

  return {
    /** Newest open cases across every practice: completed ones are dropped, the rest sorted by last update. */
    async listRecent(take = 6) {
      const perPractice = await fetchAll({ take: RECENT_FETCH_PER_PRACTICE });
      return perPractice.flat().filter((c) => !isCompleted(c)).sort(byNewest).slice(0, take);
    },

    /** A case number is unique across practices: check what is already cached, then the selected practice, then every other one. */
    async findByRefNo(refNo) {
      const wanted = String(refNo).trim();
      if (!wanted) return null;
      const isMatch = (c: CaseSummary) => c.caseRefNo === wanted || c.caseRefNo === wanted.padStart(4, '0');

      const cached = cachedCases().find(isMatch);
      if (cached) return cached;

      const ordered = practice
        ? [practice, ...practices.filter((p) => p.practiceId !== practice.practiceId)]
        : practices;
      for (const scope of ordered) {
        const cases = await fetchCases({ search: wanted, take: 10 }, scope);
        const found = cases.find(isMatch);
        if (found) return found;
      }
      return null;
    },

    /** Cases in one status across every practice, newest first. */
    async listByStatus(statusFilter, take = 6) {
      const perPractice = await fetchAll({ statusFilter, take });
      return perPractice.flat().sort(byNewest).slice(0, take);
    },

    invalidate() {
      cache.clear();
    },
  };
}
